// NetSuiteVendorReviewPopup.tsx
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { makeStyles } from 'tss-react/mui';

import CommonButton from './buttons/CommonButton';
import ConfirmationPopup from './ConfirmationPopup';
import { useState } from 'react';
import { setVendorExternalVendorId } from '../api/vendorApi/vendorAccountsApi';
import useSnackBar from '../hooks/useSnackBar';
import { AxiosError } from 'axios';
import { ErrorData } from '../types';
import { NetSuiteVendor } from '../views/VendorsView/types';
import { accountingVendorType } from '../views/VendorsView/helpers';
const VLAW_VENDOR_CREATION_ROUTE = '/vendors/add-vendor-account';

const useStyles = makeStyles()((theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      width: '1100px',
      maxWidth: 'calc(100vw - 40px)',
      maxHeight: '95vh',
      overflowY: 'auto',
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      boxSizing: 'border-box',
      borderRadius: '16px',
      padding: '32px',

      scrollbarWidth: 'thin',
      scrollbarColor: `${theme.palette.secondary.dark} transparent`,

      '&::-webkit-scrollbar': {
        width: '5px',
      },
      '&::-webkit-scrollbar-thumb': {
        borderRadius: '20px',
        backgroundColor: theme.palette.secondary.dark,
      },
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 700,
    fontFamily: 'Inter',
  },
  subtitle: {
    marginTop: '10px',
    color: theme.palette.secondary.main,
    fontSize: '16px',
    fontFamily: 'Inter',
    fontWeight: 500,
  },
  closeButton: {
    color: theme.palette.secondary.main,
    padding: 0,
  },
  topRow: {
    margin: '20px 0px 20px 0px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: theme.palette.secondary.main,
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Inter',
  },
  helperText: {
    color: theme.palette.secondary.main,
    fontSize: '12px',
    fontFamily: 'Inter',
    textAlign: 'right',
  },
  card: {
    marginTop: '18px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    padding: '16px',
    backgroundColor: theme.palette.primary.main,
  },
  sectionTitle: {
    color: theme.palette.secondary.main,
    fontSize: '16px',
    fontWeight: 600,
    fontFamily: 'Inter',
    marginBottom: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px 70px',

    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
      gap: '18px',
    },
  },
  fullWidthItem: {
    gridColumn: '1 / -1',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '7px',
    minWidth: 0,
  },
  label: {
    color: theme.palette.secondary.main,
    fontSize: '12px',
    fontWeight: 700,
    fontFamily: 'Inter',
  },
  value: {
    color: theme.palette.common.white,
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  warningBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '12px 16px',
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: '8px',
    color: theme.palette.warning.main,
  },
  warningText: {
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: 'Inter',
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '24px',
  },
}));

interface NetSuiteVendorReviewPopupProps {
    open: boolean;
    lienName: string;
    lienId?: number;
    vendor: NetSuiteVendor | null;
    onClose: () => void;
    onBack: () => void;
    synced?: 'isSynced' | 'unsynced' | null;
    onVendorSelect?: (vendorName: string, vendorId: number, vendorInternalId?: string) => void;
    onFindVendorSelect?: (vendorData: NetSuiteVendor) => void;
    onSyncOrRelink?: () => void;
    syncAction?: 'sync' | 'relink';
     warningMessage?: string;
}

const formatProviderDataLabel = (key: string): string => {
  const normalized = key
    .replace(/_/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim();

  return normalized
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ');
};

const formatProviderDataValue = (value: unknown): string | number => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return value;

  if (Array.isArray(value)) {
    const items = value
      .filter((item) => item !== null && item !== undefined && item !== '')
      .map((item) => (typeof item === 'object' ? JSON.stringify(item) : String(item)));
    return items.length ? items.join(', ') : '-';
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const getProviderStringValue = (providerData: Record<string, unknown>, key: string): string => {
  const value = providerData[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

const normalizeDynamicKey = (key: string): string => key.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

const getProviderDataOverrides = (
  vendor: NetSuiteVendor,
  providerData: Record<string, unknown>,
): Partial<NetSuiteVendor> => {
  const normalizedProviderData: Record<string, unknown> = Object.entries(providerData).reduce(
    (acc, [key, value]) => {
      acc[normalizeDynamicKey(key)] = value;
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const overrides: Partial<NetSuiteVendor> = {};
  const assignOverride = (key: string, value: string | number | boolean) => {
    (overrides as Record<string, unknown>)[key] = value;
  };

  (Object.keys(vendor) as Array<keyof NetSuiteVendor>).forEach((key) => {
    const keyName = String(key);
    const providerValue = normalizedProviderData[normalizeDynamicKey(keyName)];

    if (providerValue === undefined || providerValue === null || providerValue === '') return;

    if (keyName === 'entityId') {
      if (typeof providerValue === 'string' || typeof providerValue === 'number') {
        assignOverride(keyName, String(providerValue));
      }
      return;
    }

    const vendorValue = vendor[key];

    if (typeof vendorValue === 'boolean') {
      if (typeof providerValue === 'boolean') {
        assignOverride(keyName, providerValue);
      }
      return;
    }

    if (typeof vendorValue === 'number') {
      if (typeof providerValue === 'number') {
        assignOverride(keyName, providerValue);
      }
      return;
    }

    if (typeof vendorValue === 'string') {
      if (typeof providerValue === 'string') {
        assignOverride(keyName, providerValue);
      }
      return;
    }

    if (vendorValue === undefined || vendorValue === null) {
      if (
        typeof providerValue === 'string' ||
        typeof providerValue === 'number' ||
        typeof providerValue === 'boolean'
      ) {
        assignOverride(keyName, providerValue);
      }
    }
  });

  return overrides;
};

const InfoItem = ({
  label,
  value,
  fullWidth,
}: {
  label: string;
  value?: string | number | null;
  fullWidth?: boolean;
}) => {
  const styles = useStyles();

  return (
    <Box className={`${styles.classes.item} ${fullWidth ? styles.classes.fullWidthItem : ''}`}>
      <Typography className={styles.classes.label}>{label}</Typography>
      <Typography className={styles.classes.value}>{value || '-'}</Typography>
    </Box>
  );
};

const NetSuiteVendorReviewPopup = ({
  open,
  lienName,
  vendor,
  onClose,
  onBack,
  onVendorSelect,
  onFindVendorSelect,
  synced,
  lienId,
  onSyncOrRelink,
  syncAction = 'sync',
  warningMessage,
}: NetSuiteVendorReviewPopupProps) => {
  const styles = useStyles();
  const { setAlert } = useSnackBar();

  const [syncConfirmationOpen, setSyncConfirmationOpen] = useState(false);
  const [selectedNetSuiteVendorId, setSelectedNetSuiteVendorId] = useState('');

  const vendorType = accountingVendorType;
  
  const handleConfirmSync = async () => {
    if (!lienId || !selectedNetSuiteVendorId) return;

    try {
      const response = await setVendorExternalVendorId(lienId, selectedNetSuiteVendorId);
      setSyncConfirmationOpen(false);
      if (response.data.isSynced) {
        setAlert(() => ({
          message: 'Vendor synced successfully.',
          type: 'success',
        }));
        if (onVendorSelect) {
          onVendorSelect &&
            onVendorSelect(
              vendor?.nativeName || '',
              vendor?.nativeEntityId || 0,
              vendor?.internalId,
            );
        }
        // else if (onFindVendorSelect) {
        //     onFindVendorSelect(vendor as NetSuiteVendor);
        // }
        setSelectedNetSuiteVendorId('');
        onClose();
      } else {
        setAlert(() => ({
          message: 'Vendor sync failed.',
          type: 'error',
        }));
      }
    } catch (error) {
      const axiosError = error as AxiosError<ErrorData>;
      setAlert(() => ({
        message: axiosError.response?.data.message || 'Failed to sync vendor.',
        type: 'error',
      }));
    }
  };

  if (!vendor) return null;

  const providerData =
    vendor.providerData &&
    typeof vendor.providerData === 'object' &&
    !Array.isArray(vendor.providerData)
      ? vendor.providerData
      : {};

  const providerOverrides = getProviderDataOverrides(vendor, providerData);

  const providerMetaItems = [
    ...Object.entries(providerData).map(([key, value]) => ({
      label: formatProviderDataLabel(key),
      value: formatProviderDataValue(value),
    })),
  ];

  const primaryAddress = {
    addressLine1: vendor.address1 || '',
    addressLine2: vendor.address2 || '',
    city: vendor.city || '',
    state: vendor.state || '',
    zip: vendor.zip || '',
    country: vendor.country || '',
  };

  const mappedTaxId =
    getProviderStringValue(providerData, 'taxId') ||
    getProviderStringValue(providerData, 'taxIdLast4');

  const handleContinue = () => {
    const url = VLAW_VENDOR_CREATION_ROUTE;

    const vendorWithProviderOverrides: NetSuiteVendor = {
      ...vendor,
      ...providerOverrides,
    };

    const data: NetSuiteVendor = {
      ...vendorWithProviderOverrides,
      address1: vendorWithProviderOverrides.address1 || '',
      address2: vendorWithProviderOverrides.address2 || '',
      city: vendorWithProviderOverrides.city || '',
      state: vendorWithProviderOverrides.state || '',
      zip: vendorWithProviderOverrides.zip || '',
      country: vendorWithProviderOverrides.country || '',
      providerData: vendor.providerData,
    };

    if (onVendorSelect) {
      // Save data temporarily
      sessionStorage.setItem('netsuiteVendorData', JSON.stringify(data));

      // Open new tab
      window.open(url, '_blank');
    } else if (onFindVendorSelect) {
      onFindVendorSelect(data);
    }
  };

  const handleSync = () => {
    setSelectedNetSuiteVendorId(vendor.internalId);
    setSyncConfirmationOpen(true);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        className={styles.classes.popupContainer}
        maxWidth={false}
      >
        <Box className={styles.classes.header}>
          <Box>
            <Box className={styles.classes.titleRow}>
              <BusinessIcon />
              <Typography className={styles.classes.title}>Find vendor in {vendorType}</Typography>
            </Box>

            <Typography className={styles.classes.subtitle}>Lien - {lienName}</Typography>
          </Box>

          <IconButton onClick={onClose} className={styles.classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.classes.topRow}>
          <Box className={styles.classes.backButton} onClick={onBack}>
            <ArrowBackIcon sx={{ fontSize: 18 }} />
            Back to results
          </Box>

                {!onSyncOrRelink && <Typography className={styles.classes.helperText}>
                    You can edit fields on the next page before creating the V.Law record.
                </Typography>}
            </Box>

        <DialogContent sx={{ padding: 0 }}>
           {warningMessage && (
          <Box className={styles.classes.warningBox}>
            <WarningAmberIcon sx={{ fontSize: 20 }} />
            <Typography className={styles.classes.warningText}>{warningMessage}</Typography>
          </Box>
        )}
          <Box className={styles.classes.card}>
            <Typography className={styles.classes.sectionTitle}>VLaw Vendor Record</Typography>

            <Box className={styles.classes.grid}>
              <InfoItem label="Name" value={vendor.name} fullWidth />
              <InfoItem label="Tax ID" value={mappedTaxId} />
              <InfoItem label="Email" value={vendor.email} />
              <InfoItem label="Address line 1" value={primaryAddress?.addressLine1} fullWidth />
              <InfoItem label="City" value={primaryAddress?.city} />
              <InfoItem label="State" value={primaryAddress?.state} />
              <InfoItem label="ZIP" value={primaryAddress?.zip} />
            </Box>
          </Box>

          <Box className={styles.classes.card}>
            <Typography className={styles.classes.sectionTitle}>
              {vendorType} Meta Data (Read Only)
            </Typography>

            <Box className={styles.classes.grid}>
              {providerMetaItems.map((item, index) => (
                <InfoItem key={`${item.label}-${index}`} label={item.label} value={item.value} />
              ))}
            </Box>
          </Box>
        </DialogContent>

        <Box className={styles.classes.footer}>
          <CommonButton
            type="infoDark"
            buttonText="Cancel"
            minWidth="100px"
            onButtonClick={onClose}
          />

        {/* <Box
                        sx={{
                            '& button': {
                                height: '46px',
                                minHeight: '32px',
                            },
                        }}
                    > */}
                {onSyncOrRelink ? (
                    <CommonButton
                        type="infoMain"
                        buttonText={syncAction === 'sync' ? 'Sync' : 'Relink'}
                        minWidth="100px"
                        buttonIcon={<SyncIcon />}
                        onButtonClick={onSyncOrRelink}
                    />
                ) : (
                    <CommonButton
                        type="infoMain"
                        buttonText="Continue to V.Law vendor creation"
                        buttonIcon={<CheckCircleOutlineIcon />}
                        onButtonClick={handleContinue}
                    />
                )}
                {/* </Box> */}

          {synced === 'unsynced' && (
            <CommonButton
              type="infoMain"
              buttonText={`Sync with ${vendorType}`}
              minWidth="100px"
              buttonIcon={<SyncIcon />}
              onButtonClick={handleSync}
            />
          )}
        </Box>
      </Dialog>

      <ConfirmationPopup
        open={syncConfirmationOpen}
        title={'Sync'}
        message={`Are you sure you want to sync this Vendor ${lienName} with ${vendorType} Vendor ${vendor.name}?`}
        confirmText={'Sync'}
        onConfirm={handleConfirmSync}
        onCancel={() => {
          setSyncConfirmationOpen(false);
          setSelectedNetSuiteVendorId('');
        }}
      />
    </>
  );
};

export default NetSuiteVendorReviewPopup;